import { CheckIcon, PlusIcon } from 'lucide-react';
import { Button, EmptyState, Pill, SelectInput } from '../../index.js';

/** One item on a checklist, as the workspace endpoint returns it. */
export interface ChecklistItem {
  readonly id: string;
  readonly title: string;
  readonly status: string;
  /** Set on the fixed venue items; null on anything a client added. */
  readonly responsibilityKey: string | null;
  /** The heading the client filed it under; null for the fixed items and old rows. */
  readonly section: string | null;
  readonly responsibleName: string | null;
  readonly notes: string | null;
  readonly dueAt: string | null;
  readonly overdue: boolean;
  readonly approvalRequired: boolean;
}

/** A section the list always shows, keyed to seeded items. */
export interface FixedSection {
  readonly title: string;
  /** Who usually handles it — shown under the heading. */
  readonly who: string;
  readonly items: readonly { readonly key: string; readonly hint: string }[];
}

export interface SectionedChecklistProps {
  readonly items: readonly ChecklistItem[];
  /** Sections that exist before anything is added (the venue list's four). */
  readonly fixedSections?: readonly FixedSection[];
  /** The statuses the selector offers, in order, with their labels. */
  readonly statuses: readonly (readonly [string, string])[];
  /** What the tick sets, and what un-ticking sets. */
  readonly doneStatus: string;
  readonly undoneStatus: string;
  /** Statuses that count as dealt with, for the progress figures. */
  readonly settledStatuses: ReadonlySet<string>;
  /** Where items with no section land. */
  readonly fallbackSectionTitle: string;
  /**
   * Changes an item's status. Omit for someone who may only read the list:
   * the tick and the status picker become plain words.
   */
  readonly onSetStatus?: ((itemId: string, status: string) => void) | undefined;
  /** Opens the add dialog with this section chosen. Absent on a list nobody adds to by hand. */
  readonly onAddItem?: ((section: string) => void) | undefined;
  /** Opens the add dialog asking for a new section name. Absent on a fixed list. */
  readonly onAddSection?: (() => void) | undefined;
  /** Opens the same dialog on an existing item. Absent when items are not editable here. */
  readonly onEdit?: ((item: ChecklistItem) => void) | undefined;
  /**
   * The pill a row carries in place of the plain "overdue" one, for a list
   * that knows more about a row than its date (the event plan colors by
   * owner). Return null for a row that needs no pill.
   */
  readonly itemPill?: ((item: ChecklistItem) => React.ReactNode) | undefined;
  readonly isSaving: boolean;
  readonly emptyTitle: string;
  readonly emptyMessage: string;
}

/**
 * A due date written out, read as a calendar date: "Oct 1, 2026". Only the
 * date half is read, so a date stored as midnight UTC does not show as the
 * day before west of Greenwich.
 */
function dueDate(iso: string): string {
  const parsed = new Date(`${iso.slice(0, 10)}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? iso.slice(0, 10)
    : parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * The section names a list currently has, fixed ones first — what the add
 * dialog offers as destinations.
 *
 * @param items - The list's items.
 * @param fixedSections - Its fixed sections, if any.
 * @returns Titles in display order, without duplicates.
 */
export function sectionTitlesOf(
  items: readonly ChecklistItem[],
  fixedSections: readonly FixedSection[] = [],
): readonly string[] {
  const seen = new Set<string>(fixedSections.map((section) => section.title));
  const titles = [...seen];
  for (const item of items) {
    if (item.section && !seen.has(item.section)) {
      seen.add(item.section);
      titles.push(item.section);
    }
  }
  return titles;
}

/** Progress for a set of items. */
export function checklistProgress(
  items: readonly ChecklistItem[],
  settledStatuses: ReadonlySet<string>,
): { settled: number; total: number } {
  return {
    settled: items.filter((item) => settledStatuses.has(item.status)).length,
    total: items.length,
  };
}

interface RenderedSection {
  readonly title: string;
  readonly who: string | null;
  readonly rows: readonly ChecklistItem[];
  readonly hints: ReadonlyMap<string, string>;
}

/**
 * Groups items into sections: the fixed sections by item key, then every
 * section a client named in first-seen order, then the fallback for whatever
 * has neither.
 */
function sectionsOf(
  items: readonly ChecklistItem[],
  fixedSections: readonly FixedSection[],
  fallbackTitle: string,
): readonly RenderedSection[] {
  const keyToSection = new Map<string, number>();
  fixedSections.forEach((section, index) =>
    section.items.forEach((item) => keyToSection.set(item.key, index)),
  );

  const fixed: RenderedSection[] = fixedSections.map((section) => ({
    title: section.title,
    who: section.who,
    rows: items.filter(
      (item) =>
        item.responsibilityKey !== null &&
        keyToSection.get(item.responsibilityKey) === fixedSections.indexOf(section),
    ),
    hints: new Map(section.items.map((item) => [item.key, item.hint])),
  }));

  const rest = items.filter(
    (item) => item.responsibilityKey === null || !keyToSection.has(item.responsibilityKey),
  );
  const named = new Map<string, ChecklistItem[]>();
  for (const item of rest) {
    const title = item.section ?? fallbackTitle;
    named.set(title, [...(named.get(title) ?? []), item]);
  }
  // A named section that matches a fixed one is the client adding to it.
  const dynamic: RenderedSection[] = [];
  for (const [title, rows] of named) {
    const existing = fixed.find((section) => section.title === title);
    if (existing) {
      fixed[fixed.indexOf(existing)] = { ...existing, rows: [...existing.rows, ...rows] };
    } else {
      dynamic.push({ title, who: null, rows, hints: new Map() });
    }
  }
  // The fallback reads last, whatever order it was first seen in.
  dynamic.sort((a, b) => Number(a.title === fallbackTitle) - Number(b.title === fallbackTitle));
  return [...fixed, ...dynamic];
}

/**
 * A checklist made of sections, each with its own count and its own "add"
 * — the one shape both the planning list and the venue list take. A tick is
 * the main control; the fuller set of statuses stays in the selector on the
 * right for the item that is asked-but-not-answered or not needed at all.
 *
 * @param props - The items, how statuses work on this list, and the actions.
 * @returns The checklist.
 */
export function SectionedChecklist({
  items,
  fixedSections = [],
  statuses,
  doneStatus,
  undoneStatus,
  settledStatuses,
  fallbackSectionTitle,
  onSetStatus,
  onAddItem,
  onAddSection,
  onEdit,
  itemPill,
  isSaving,
  emptyTitle,
  emptyMessage,
}: SectionedChecklistProps): React.ReactElement {
  const sections = sectionsOf(items, fixedSections, fallbackSectionTitle).filter(
    (section) => section.rows.length > 0 || fixedSections.some((f) => f.title === section.title),
  );
  const { settled, total } = checklistProgress(items, settledStatuses);
  const share = total === 0 ? 0 : Math.round((settled / total) * 100);

  if (sections.length === 0) {
    return (
      <div className="px-5 pb-5">
      <EmptyState
        icon={<PlusIcon aria-hidden />}
        title={emptyTitle}
        message={emptyMessage}
        action={
          onAddSection ? (
            <Button onClick={onAddSection} disabled={isSaving}>
              Add the first section
            </Button>
          ) : undefined
        }
      />
      </div>
    );
  }

  return (
    <div>
      {total > 0 ? (
        <div className="px-5 pb-4">
          <div className="flex items-baseline justify-between text-portal-tiny">
            <span className="font-bold">
              {settled} of {total} dealt with
            </span>
            <span className="text-portal-muted">{share}%</span>
          </div>
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={settled}
            aria-label="Checklist progress"
            className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100"
          >
            <div
              className="h-full rounded-full bg-brand transition-[width]"
              style={{ width: `${share}%` }}
            />
          </div>
        </div>
      ) : null}

      {sections.map((section) => {
        const done = section.rows.filter((row) => settledStatuses.has(row.status)).length;
        return (
          <section key={section.title}>
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-portal-line bg-portal-sky/40 px-5 py-2.5">
              <div>
                <h3 className="text-portal-small font-bold">{section.title}</h3>
                {section.who ? (
                  <p className="text-portal-micro text-portal-muted">{section.who}</p>
                ) : null}
              </div>
              <div className="flex items-center gap-3">
                {section.rows.length > 0 ? (
                  <span className="text-portal-micro text-portal-muted">
                    {done} of {section.rows.length}
                  </span>
                ) : null}
                {onAddItem ? (
                  <Button
                    variant="secondary"
                    size="compact"
                    disabled={isSaving}
                    onClick={() => onAddItem(section.title)}
                  >
                    <PlusIcon aria-hidden className="size-4" />
                    Add item
                  </Button>
                ) : null}
              </div>
            </div>

            {section.rows.length === 0 ? (
              <p className="border-t border-portal-line px-5 py-3 text-portal-tiny text-portal-muted">
                Nothing here yet.
              </p>
            ) : (
              <ul className="divide-y divide-portal-line border-t border-portal-line">
                {section.rows.map((row) => {
                  const ticked = row.status === doneStatus;
                  const settledRow = settledStatuses.has(row.status);
                  const hint =
                    row.responsibilityKey !== null
                      ? section.hints.get(row.responsibilityKey)
                      : undefined;
                  const meta = [
                    row.responsibleName,
                    row.dueAt ? `Due ${dueDate(row.dueAt)}` : null,
                    row.notes,
                  ].filter(Boolean);
                  return (
                    <li key={row.id} className="flex items-center gap-3 px-5 py-3">
                      {onSetStatus === undefined ? (
                        <span
                          role="img"
                          aria-label={ticked ? 'Done' : 'Not done'}
                          className={[
                            'grid size-6 shrink-0 place-items-center rounded-full border-2',
                            ticked ? 'border-brand bg-brand text-white' : 'border-portal-line bg-white text-transparent',
                          ].join(' ')}
                        >
                          <CheckIcon aria-hidden className="size-3.5" strokeWidth={3} />
                        </span>
                      ) : (
                        <button
                          type="button"
                          role="checkbox"
                          aria-checked={ticked}
                          aria-label={`${row.title}: ${ticked ? 'done' : 'not done'}`}
                          disabled={isSaving}
                          onClick={() => onSetStatus(row.id, ticked ? undoneStatus : doneStatus)}
                          className={[
                            'grid size-6 shrink-0 place-items-center rounded-full border-2 transition-colors',
                            ticked
                              ? 'border-brand bg-brand text-white'
                              : 'border-portal-line bg-white text-transparent hover:border-brand',
                          ].join(' ')}
                        >
                          <CheckIcon aria-hidden className="size-3.5" strokeWidth={3} />
                        </button>
                      )}

                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span
                            className={[
                              'text-portal-small font-bold',
                              settledRow ? 'text-portal-muted' : '',
                            ].join(' ')}
                          >
                            {row.title}
                          </span>
                          {itemPill ? itemPill(row) : row.overdue ? <Pill tone="danger">overdue</Pill> : null}
                          {row.approvalRequired ? <Pill tone="warning">needs approval</Pill> : null}
                        </span>
                        {hint ? (
                          <span className="block text-portal-micro text-portal-muted">{hint}</span>
                        ) : null}
                        {meta.length > 0 ? (
                          <span className="block text-portal-micro text-portal-muted">
                            {meta.join(' · ')}
                          </span>
                        ) : null}
                      </span>

                      {onEdit ? (
                        <Button
                          variant="secondary"
                          size="compact"
                          disabled={isSaving}
                          onClick={() => onEdit(row)}
                          aria-label={`Edit ${row.title}`}
                          className="shrink-0"
                        >
                          Edit
                        </Button>
                      ) : null}

                      {onSetStatus === undefined ? (
                        <span className="w-40 shrink-0 text-right text-portal-small text-portal-muted">
                          {statuses.find(([value]) => value === row.status)?.[1] ?? row.status}
                        </span>
                      ) : (
                        <SelectInput
                          density="compact"
                          aria-label={`${row.title} status`}
                          value={row.status}
                          disabled={isSaving}
                          onChange={(event) => onSetStatus(row.id, event.target.value)}
                          className="w-40 shrink-0"
                        >
                          {statuses.map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </SelectInput>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
